import React, { memo, useMemo, useRef, useState } from 'react';
import {
  Box, IconButton, Input, Popover, Skeleton, Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SearchIcon from '@mui/icons-material/Search';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useDispatch, useSelector } from 'react-redux';
import { updateState } from '../../../redux/commonSlice';
import { useOrganisationsList } from '../hooks/useOrganisationsList';
import useCustomTheme from '../../../theme/useCustomTheme';

const OrgSelectorDropdown = ({ selectedOrganisation, setSelectedOrganisation }) => {
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const dispatch = useDispatch();
  const [theme] = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchString, setSearchString] = useState('');
  const searchRef = useRef(null);

  const { data: orgListData, isLoading: isOrgsLoading } = useOrganisationsList();

  const organisationList = useMemo(() =>
    (orgListData?.length > 0 ? orgListData : null) ?? commonReducer.organisation_list ?? [],
    [orgListData, commonReducer.organisation_list]
  );

  const displayedOrgs = useMemo(() => {
    if (!searchString) return organisationList;
    return organisationList.filter(org =>
      org.name?.toLowerCase().includes(searchString.toLowerCase())
    );
  }, [organisationList, searchString]);

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setSearchString('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchString('');
  };

  const handleSelect = (org) => {
    setSelectedOrganisation(org);
    dispatch(updateState({ selected_organisation: org }));
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOrganisation('');
    dispatch(updateState({ selected_organisation: null }));
  };

  const label = selectedOrganisation?.name || 'Select Organisation';
  const hasSelection = !!selectedOrganisation?.id;

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: { xs: 1, lg: 1.25 },
          py: 0.5,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: hasSelection ? `${theme.palette.primary.main}60` : '#D0D3DB',
          backgroundColor: hasSelection ? `${theme.palette.primary.main}0d` : '#F8F9FB',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.15s ease',
          maxWidth: { xs: '140px', sm: '180px', lg: '220px' },
          '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: `${theme.palette.primary.main}12` },
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: { xs: '10px', lg: '12px' },
            fontFamily: 'Rubik',
            fontWeight: hasSelection ? 600 : 400,
            color: hasSelection ? '#1E1E2D' : '#888',
            flex: 1,
            minWidth: 0,
          }}
        >
          {label}
        </Typography>

        {hasSelection ? (
          <CloseRoundedIcon
            onClick={handleClear}
            sx={{ fontSize: { xs: '12px', lg: '14px' }, color: '#999', flexShrink: 0, '&:hover': { color: '#444' } }}
          />
        ) : (
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: { xs: '14px', lg: '16px' },
              color: '#999',
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            width: '240px',
            mt: 0.5,
            overflow: 'hidden',
          },
        }}
      >
        {/* Search */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: '1px solid #F0F1F3' }}>
          <Input
            inputRef={searchRef}
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            placeholder="Search organisations…"
            disableUnderline
            startAdornment={
              <SearchIcon sx={{ fontSize: '14px', color: '#aaa', mr: 0.75 }} />
            }
            sx={{
              width: '100%',
              fontSize: '12px',
              fontFamily: 'Rubik',
              backgroundColor: '#F8F9FB',
              borderRadius: '6px',
              px: 1,
              py: 0.5,
              '& input': { fontSize: '12px', fontFamily: 'Rubik', p: 0 },
            }}
          />
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: '360px', overflowY: 'auto', py: 0.5 }}>
          {isOrgsLoading && organisationList.length === 0
            ? [1, 2, 3].map((i) => (
                <Box key={i} sx={{ px: 2, py: 1 }}>
                  <Skeleton variant="rounded" height="12px" width="70%" />
                </Box>
              ))
            : displayedOrgs.length === 0
            ? <Typography sx={{ fontSize: '11px', fontFamily: 'Rubik', color: '#aaa', textAlign: 'center', py: 3 }}>
                No organisations found
              </Typography>
            : displayedOrgs.map((org) => {
                const isSelected = org.id === selectedOrganisation?.id;
                return (
                  <Box
                    key={org.id}
                    onClick={() => handleSelect(org)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 0.875,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? `${theme.palette.primary.main}18` : 'transparent',
                      '&:hover': { backgroundColor: isSelected ? `${theme.palette.primary.main}22` : '#F5F6F8' },
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: { xs: '11px', lg: '12px' },
                        fontFamily: 'Rubik',
                        fontWeight: isSelected ? 600 : 400,
                        color: '#1E1E2D',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {org.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
                      {org?.is_event_changed && (
                        <FiberManualRecordIcon sx={{ fontSize: '10px', color: '#00ceff' }} />
                      )}
                      {isSelected && (
                        <CheckRoundedIcon sx={{ fontSize: '14px', color: theme.palette.primary.main }} />
                      )}
                    </Box>
                  </Box>
                );
              })
          }
        </Box>
      </Popover>
    </>
  );
};

export default memo(OrgSelectorDropdown);
